const ALARM_NULL_END_DATE = '0001-01-01T00:00:00Z';

const ALERT_API_STATES = {
  ACTIVE: 'ALARM_STATE_ACTIVE',
  CLEAR: 'ALARM_STATE_CLEAR'
};

const ALARM_SUPPRESSED_FILTER = {
  EXCLUDED: 'SUPPRESSED_EXCLUDED',
  INCLUDED: 'SUPPRESSED_INCLUDED',
  ONLY: 'SUPPRESSED_ONLY'
};

const ALERT_UI_STATES = {
  ACTIVE: 'alarm',
  CLEAR: 'clear'
};

const ALERT_STATE_UI_TO_API = {
  [ALERT_UI_STATES.ACTIVE]: ALERT_API_STATES.ACTIVE,
  [ALERT_UI_STATES.CLEAR]: ALERT_API_STATES.CLEAR
};

const ALERT_STATE_API_TO_UI = {
  [ALERT_API_STATES.ACTIVE]: ALERT_UI_STATES.ACTIVE,
  [ALERT_API_STATES.CLEAR]: ALERT_UI_STATES.CLEAR
};

const EXTERNAL_SYSTEMS = {
  AI_INVESTIGATION: 'EXTERNAL_SYSTEM_AI_INVESTIGATION',
  SERVICENOW: 'EXTERNAL_SYSTEM_SERVICENOW',
  JIRA_CLOUD: 'EXTERNAL_SYSTEM_JIRA_CLOUD'
};

const ALERT_TABLE_COLUMNS = {
  TYPE: 'type',
  APPLICATION: 'applicationLabel',
  STATE: 'state',
  STATE_LABEL: 'stateLabel',
  STATUS: 'status',
  SEVERITY: 'severity',
  POLICY_NAME: 'policyName',
  POLICY_ID: 'policyID',
  MITIGATION_ID: 'mitigationID',
  START_TIME: 'startTime',
  END_TIME: 'endTime',
  DURATION: 'duration',
  PRIMARY_DIMENSION: 'primaryDimension',
  METRIC: 'metric',
  ID: 'id',
  TENANT: 'tenant',
  SILENCE_MODEL: 'silenceModel',
  ACK_STATE_LABEL: 'ackStateLabel',
  AI_INVESTIGATION: 'aiInvestigation',
  // Additional columns: only tables that list them in `additionalColumns` have them.
  SUPPRESSED: 'isSuppressed',
  ARCHIVED: 'isArchived'
};

const ALERT_BASELINE_REASONS = {
  // Non directional
  ACT_NOT_USED_BASELINE: {
    title: 'Used static threshold',
    message: 'The static threshold value was used (no baseline).'
  },
  // Current to History (Typical)
  ACT_BASELINE_MISSING_SKIP: {
    title: 'Missing baseline value, skipped',
    message: 'No baseline value was found for this key, so this evaluation was skipped.'
  },
  ACT_BASELINE_MISSING_TRIGGER: {
    title: 'Missing baseline value, treated as met',
    message: 'No baseline value was found for this key, so we treated the baseline condition as met.'
  },
  ACT_BASELINE_USED_FOUND: {
    title: 'Used baseline value',
    message: 'The value met the baseline condition.'
  },
  ACT_BASELINE_MISSING_DEFAULT: {
    title: 'Missing baseline value, used default',
    message: 'No baseline value was found for this key, so we used the default value.'
  },
  ACT_BASELINE_MISSING_LOWEST: {
    title: 'Missing baseline value, used lowest peer',
    message: 'No baseline value was found for this key, so we used the lowest value among peer keys.'
  },
  ACT_BASELINE_MISSING_HIGHEST: {
    title: 'Missing baseline value, used highest peer',
    message: 'No baseline value was found for this key, so we used the highest value among peer keys.'
  },
  ACT_BASELINE_NOT_FOUND_EXISTS: {
    title: 'Missing baseline key (joined top keys)',
    message: 'Key was not found in baseline top keys, but was present in the current top keys.'
  },
  ACT_BASELINE_MISSING_DEFAULT_INSTEAD_OF_LOWEST: {
    title: 'Used default (missing baseline value, lowest peer)',
    message:
      'No baseline value was found for this key, and there was no lowest peer key available, so we used the default value.'
  },
  ACT_BASELINE_MISSING_DEFAULT_INSTEAD_OF_HIGHEST: {
    title: 'Used default (missing baseline value, highest peer)',
    message:
      'No baseline value was found for this key, and there was no highest peer key available, so we used the default value.'
  },
  // History to Current (Inverse)
  ACT_CURRENT_MISSING_SKIP: {
    title: 'Missing current value, skipped',
    message: 'No current value was found for this key, so this evaluation was skipped.'
  },
  ACT_CURRENT_MISSING_TRIGGER: {
    title: 'Missing current value, treated as met',
    message: 'No current value was found for this key, so we treated the baseline condition as met.'
  },
  ACT_CURRENT_USED_FOUND: {
    title: 'Used current value',
    message: 'The value met the baseline condition.'
  },
  ACT_CURRENT_MISSING_DEFAULT: {
    title: 'Missing current value, used default',
    message: 'No current value was found for this key, so we used the default value.'
  },
  ACT_CURRENT_MISSING_LOWEST: {
    title: 'Missing current value, used lowest peer',
    message: 'No current value was found for this key, so we used the lowest value among peer keys.'
  },
  ACT_CURRENT_MISSING_HIGHEST: {
    title: 'Missing current value, used highest peer',
    message: 'No current value was found for this key, so we used the highest value among peer keys.'
  },
  ACT_CURRENT_NOT_FOUND_EXISTS: {
    title: 'Missing current key (left top keys)',
    message: 'Key was not found in current top keys, but was present in baseline top keys.'
  },
  ACT_CURRENT_MISSING_DEFAULT_INSTEAD_OF_LOWEST: {
    title: 'Used default (missing current value, lowest peer)',
    message:
      'No current value was found for this key, and there was no lowest peer key available, so we used the default value.'
  },
  ACT_CURRENT_MISSING_DEFAULT_INSTEAD_OF_HIGHEST: {
    title: 'Used default (missing current value, highest peer)',
    message:
      'No current value was found for this key, and there was no highest peer key available, so we used the default value.'
  }
};

const BASELINE_FALLBACK_OPS = {
  DEFAULT: 'useThisValue',
  SKIP: 'skip',
  TRIGGER: 'trigger',
  LOWEST: 'lowest',
  HIGHEST: 'highest'
};

const BASELINE_FALLBACK_OP_LABELS = {
  [BASELINE_FALLBACK_OPS.DEFAULT]: 'Use a fallback value',
  [BASELINE_FALLBACK_OPS.SKIP]: 'Treat baseline condition as not met',
  [BASELINE_FALLBACK_OPS.TRIGGER]: 'Treat baseline condition as met',
  [BASELINE_FALLBACK_OPS.LOWEST]: 'Use lowest value from top keys',
  [BASELINE_FALLBACK_OPS.HIGHEST]: 'Use highest value from top keys'
};

const BASELINE_FALLBACK_OPTIONS = [
  {
    value: BASELINE_FALLBACK_OPS.DEFAULT,
    label: BASELINE_FALLBACK_OP_LABELS[BASELINE_FALLBACK_OPS.DEFAULT]
  },
  {
    value: BASELINE_FALLBACK_OPS.SKIP,
    label: BASELINE_FALLBACK_OP_LABELS[BASELINE_FALLBACK_OPS.SKIP]
  },
  {
    value: BASELINE_FALLBACK_OPS.TRIGGER,
    label: BASELINE_FALLBACK_OP_LABELS[BASELINE_FALLBACK_OPS.TRIGGER]
  },
  {
    value: BASELINE_FALLBACK_OPS.LOWEST,
    label: BASELINE_FALLBACK_OP_LABELS[BASELINE_FALLBACK_OPS.LOWEST]
  },
  {
    value: BASELINE_FALLBACK_OPS.HIGHEST,
    label: BASELINE_FALLBACK_OP_LABELS[BASELINE_FALLBACK_OPS.HIGHEST]
  }
];

const ALERT_ID_SEARCH_REGEX = /^[a-zA-Z0-9-, ]*$/;

const MAX_WINDOW_LENGTH_MIN = 6 * 60;

const ALERTING_PER_PAGE_COUNT_DEFAULT = 20;
const ALERTING_EXPORT_PER_PAGE_COUNT_DEFAULT = 1000;

const ALERT_BASELINE_REASONS_MAP = {
  0: 'ACT_NOT_USED_BASELINE',
  1: 'ACT_BASELINE_MISSING_SKIP',
  2: 'ACT_BASELINE_MISSING_TRIGGER',
  3: 'ACT_BASELINE_USED_FOUND',
  4: 'ACT_BASELINE_MISSING_DEFAULT',
  5: 'ACT_BASELINE_MISSING_LOWEST',
  6: 'ACT_BASELINE_MISSING_HIGHEST',
  7: 'ACT_BASELINE_NOT_FOUND_EXISTS',
  8: 'ACT_CURRENT_MISSING_SKIP',
  9: 'ACT_CURRENT_MISSING_TRIGGER',
  10: 'ACT_CURRENT_USED_FOUND',
  11: 'ACT_CURRENT_MISSING_DEFAULT',
  12: 'ACT_CURRENT_MISSING_LOWEST',
  13: 'ACT_CURRENT_MISSING_HIGHEST',
  14: 'ACT_CURRENT_NOT_FOUND_EXISTS',
  15: 'ACT_BASELINE_MISSING_DEFAULT_INSTEAD_OF_LOWEST',
  16: 'ACT_BASELINE_MISSING_DEFAULT_INSTEAD_OF_HIGHEST',
  17: 'ACT_CURRENT_MISSING_DEFAULT_INSTEAD_OF_LOWEST',
  18: 'ACT_CURRENT_MISSING_DEFAULT_INSTEAD_OF_HIGHEST'
};

const ALERT_CLEAR_REASONS_CODE_MAP = {
  UNSPECIFIED: 'ALARM_CLEAR_CAUSE_UNSPECIFIED',
  CLEAR_TRIGGER: 'ALARM_CLEAR_CAUSE_CLEAR_TRIGGER',
  TOO_LOW_SEVERITY_TRIGGER: 'ALARM_CLEAR_CAUSE_TOO_LOW_SEVERITY_TRIGGER',
  CRITERIA_MET: 'ALARM_CLEAR_CAUSE_CRITERIA_MET',
  MAJOR_EPOCH_BUMP: 'ALARM_CLEAR_CAUSE_MAJOR_EPOCH_BUMP',
  DEADLINE_AFTER_MINOR_EPOCH_BUMP: 'ALARM_CLEAR_CAUSE_DEADLINE_AFTER_MINOR_EPOCH_BUMP',
  ALARM_LIMIT: 'ALARM_CLEAR_CAUSE_ALARM_LIMIT',
  RULE_DISABLED: 'ALARM_CLEAR_CAUSE_RULE_DISABLED',
  RULE_ARCHIVED: 'ALARM_CLEAR_CAUSE_RULE_ARCHIVED',
  SUPPRESSED: 'ALARM_CLEAR_CAUSE_SUPPRESSED',
  CLEAR_USER_REQUESTED: 'ALARM_CLEAR_CAUSE_CLEAR_USER_REQUESTED',
  REPLACED_ON_KEY_CONFLICT: 'ALARM_CLEAR_CAUSE_REPLACED_ON_KEY_CONFLICT',
  SEVERITY_LEVEL_DISABLED: 'ALARM_CLEAR_CAUSE_SEVERITY_LEVEL_DISABLED'
};

const ALERT_CLEAR_REASONS = {
  ALARM_CLEAR_CAUSE_UNSPECIFIED: 'Unknown reason',
  ALARM_CLEAR_CAUSE_CLEAR_TRIGGER: 'Alert clearing conditions were met',
  ALARM_CLEAR_CAUSE_TOO_LOW_SEVERITY_TRIGGER: 'Incoming trigger did not match any configured severity',
  ALARM_CLEAR_CAUSE_CRITERIA_MET: 'Alert clearing conditions were met',
  ALARM_CLEAR_CAUSE_MAJOR_EPOCH_BUMP: 'Alert invalidated due to significant policy config changes',
  ALARM_CLEAR_CAUSE_DEADLINE_AFTER_MINOR_EPOCH_BUMP:
    'Alert invalidated due to policy config or device metadata updates',
  ALARM_CLEAR_CAUSE_ALARM_LIMIT: 'Policy reached alert limit',
  ALARM_CLEAR_CAUSE_RULE_DISABLED: 'Policy was disabled',
  ALARM_CLEAR_CAUSE_RULE_ARCHIVED: 'Policy was archived',
  ALARM_CLEAR_CAUSE_SUPPRESSED: 'Alert was suppressed',
  ALARM_CLEAR_CAUSE_CLEAR_USER_REQUESTED: 'Manually cleared by user',
  ALARM_CLEAR_CAUSE_REPLACED_ON_KEY_CONFLICT: 'Replaced by another active alert with the same key',
  ALARM_CLEAR_CAUSE_SEVERITY_LEVEL_DISABLED: 'Policy severity level was disabled'
};

const ALERT_CLEAR_REASONS_MAP = {
  0: 'ALARM_CLEAR_CAUSE_UNSPECIFIED',
  1: 'ALARM_CLEAR_CAUSE_CLEAR_TRIGGER',
  2: 'ALARM_CLEAR_CAUSE_TOO_LOW_SEVERITY_TRIGGER',
  3: 'ALARM_CLEAR_CAUSE_CRITERIA_MET',
  4: 'ALARM_CLEAR_CAUSE_MAJOR_EPOCH_BUMP',
  5: 'ALARM_CLEAR_CAUSE_DEADLINE_AFTER_MINOR_EPOCH_BUMP',
  6: 'ALARM_CLEAR_CAUSE_ALARM_LIMIT',
  7: 'ALARM_CLEAR_CAUSE_RULE_DISABLED',
  8: 'ALARM_CLEAR_CAUSE_RULE_ARCHIVED',
  9: 'ALARM_CLEAR_CAUSE_SUPPRESSED',
  10: 'ALARM_CLEAR_CAUSE_CLEAR_USER_REQUESTED',
  11: 'ALARM_CLEAR_CAUSE_REPLACED_ON_KEY_CONFLICT',
  12: 'ALARM_CLEAR_CAUSE_SEVERITY_LEVEL_DISABLED'
};

const ALERT_STATE_ICONS = {
  alarm: 'notifications',
  ackReq: 'confirm',
  clear: 'tick'
};

const ALERT_STATE_LABELS = {
  alarm: 'Active',
  clear: 'Cleared'
};

const ALERT_SUMMARY_OPTIONS = [
  { label: 'State', value: 'states' },
  { label: 'Severity', value: 'severities' },
  { label: 'Type', value: 'application' },
  { label: 'Policy', value: 'policies' }
];

const DIMENSION_LOOKUP_PATHS = {
  i_device_id: 'device.name',
  i_device_site_name: 'site.name',
  InterfaceID_src: 'interface.snmpDescription',
  InterfaceID_dst: 'interface.snmpDescription',
  ktappprotocol__snmp__output_port: 'interface.snmpDescription',
  ktappprotocol__snmp__i_device_name: 'device.name',
  ktappprotocol__snmp__i_device_site_name: 'site.name',
  src_nexthop_asn: 'asn.description',
  dst_nexthop_asn: 'asn.description',
  src_as: 'asn.description',
  dst_as: 'asn.description',
  AS_src: 'asn.description',
  AS_dst: 'asn.description',
  src_geo_region: 'region.name',
  dst_geo_region: 'region.name'
};

const DIMENSION_TO_LABEL = {
  cdn: 'CDN',
  i_src_cdn: 'CDN',
  src_cdn: 'CDN',
  src_as: 'Src AS Number',
  dst_as: 'Dest AS Number',
  src_geo: 'Src Country',
  dst_geo: 'Dest Country',
  inet_dst_addr: 'Dest IP Address',
  inet_src_addr: 'Src IP Address',
  device_id: 'Device',
  snmp_id: 'Interface',
  agent_alias: 'Agent',
  agent_cloud_region: 'Region',
  agent_cloud_provider: 'Cloud',
  agent_asn: 'ASN',
  agent_ip: 'IP Address',
  test_id: 'Test ID',
  test_name: 'Test Name',
  test_type: 'Test Type',
  test_target: 'Test Target',
  dst_inet_addr: 'Tested IP',
  ktsubtype__aws_subnet__str17: 'Gateway ID',
  ktsubtype__aws_subnet__str19: 'Gateway',
  capacity_plan_id: 'Capacity plan'
};

const EVALUATION_FREQUENCY_OPTIONS = [
  {
    value: 15,
    label: '15 Seconds'
  },
  {
    value: 20,
    label: '20 Seconds'
  },
  {
    value: 30,
    label: '30 Seconds'
  },
  {
    value: 60,
    label: '60 Seconds'
  },
  {
    value: 120,
    label: '2 Minutes'
  },
  {
    value: 300,
    label: '5 Minutes'
  }
];

const POLICY_BASELINE_PRESET_TYPES = {
  CUSTOM: 'custom',
  EXPRESS: 'express',
  DEFAULT: 'default',
  PRECISION: 'precision'
};

/*
  NMS Interface Up/Down policies use the Interface selector.
  However, the output from this component does not match the dimensions
  we need to match correctly on Kmetrics policies. This map is used to
  convert the output from the component to the correct dimension, as well
  as define the operator to use, and friendly names for display.
*/
const INTERFACE_FILTER_MAP = {
  interface_description: {
    dimension: 'name',
    label: 'Interface Name',
    operator: '~*'
  },
  snmp_alias: {
    dimension: 'description',
    label: 'Interface Description',
    readable: 'matches regex',
    operator: '~*'
  },
  // TODO: This isn't currently supported but we'd love to bring it back
  // interface_ip: {
  //   dimension: 'if_Address',
  //   label: 'Interface IP Address',
  //   readable: 'like',
  //   operator: 'ILIKE'
  // },
  device_id: {
    dimension: 'km_device_id',
    label: 'On devices',
    readable: 'including',
    operator: '='
  },
  device_name: {
    dimension: 'device_name',
    label: 'Devices',
    readable: 'matches regex',
    operator: '~*'
  },
  // TODO: This isn't currently supported but we'd love to bring it back
  // site_id: {
  //   dimension: 'tags.site',
  //   label: 'Site',
  //   readable: 'matches regex',
  //   operator: 'ILIKE'
  // },
  snmp_speed: {
    dimension: 'speed',
    label: 'Capacity',
    readable: 'is',
    operator: 'ILIKE'
  }
  // TODO: This isn't currently supported but we'd love to bring it back
  // interface_type: {
  //   dimension: 'type',
  //   label: 'Interface Type',
  //   readable: 'matches regex',
  //   operator: '~*'
  // }
};

const POLICY_MAX_NAME_LENGTH = 50;

const POLICY_AGG_FUNCTIONS = {
  avg: 'AGG_FUNC_AVERAGE',
  last: 'AGG_FUNC_LAST',
  min: 'AGG_FUNC_MIN',
  max: 'AGG_FUNC_MAX',
  rate: 'AGG_FUNC_RATE'
};

// Same object as POLICY_AGG_FUNCTIONS, but with key & value swapped
const POLICY_AGG_FUNCTIONS_DESERIALIZE = Object.keys(POLICY_AGG_FUNCTIONS).reduce(
  (acc, key) => ({ ...acc, [POLICY_AGG_FUNCTIONS[key]]: key }),
  {}
);

const POLICY_APPLICATION_LABELS = {
  ddos: 'Protect',
  core: 'Traffic',
  synthetics: 'Synthetics',
  kmetrics: 'NMS',
  nms: 'NMS',
  kevent: 'NMS'
};

const POLICY_APPLICATIONS = {
  DDOS: 'ddos',
  CORE: 'core',
  CLOUD: 'cloud',
  SYNTHETICS: 'synthetics',
  METRIC: 'kmetrics',
  NMS: 'nms',
  KEVENT: 'kevent'
};

// Intentionally omits: Native NMS, Synthetics, Kevent
const FLOW_POLICY_APPLICATIONS = [
  POLICY_APPLICATIONS.CORE,
  POLICY_APPLICATIONS.CLOUD, // Cloud is rolled up under CORE
  POLICY_APPLICATIONS.DDOS,
  POLICY_APPLICATIONS.METRIC
];

const ALERT_MANAGER_ACTIVATION_MODES = {
  TOGGLE: 'activationModeToggle',
  ROLLING_WINDOW: 'activationModeRollingWindow',
  UNSPECIFIED: 'activationModeUnspecified'
};

const EVENT_POLICY_TYPES = {
  SNMP_TRAP: 'snmp_traps', // Using plural under the hood to match dimension app proto
  SYSLOG: 'syslog' // This also matches the dimension app proto
};

const EVENT_POLICY_METRICS = {
  SNMP_TRAP: `ktappprotocol__event_${EVENT_POLICY_TYPES.SNMP_TRAP}__i_count`,
  SYSLOG: `ktappprotocol__event_${EVENT_POLICY_TYPES.SYSLOG}__i_count`
};

const ALERT_ACKSTATE_LABELS = {
  ALARM_ACKNOWLEDGEMENT_NOT_ACKED: 'Not Acked',
  ALARM_ACKNOWLEDGEMENT_DONE: 'Acked',
  ALARM_ACKNOWLEDGEMENT_REQUIRED: 'Ack Required',
  ACKED_BY_ME: 'Acked by Me'
};

const ALERT_ACKSTATES = {
  NOT_ACKED: 'ALARM_ACKNOWLEDGEMENT_NOT_ACKED',
  DONE: 'ALARM_ACKNOWLEDGEMENT_DONE',
  REQUIRED: 'ALARM_ACKNOWLEDGEMENT_REQUIRED',
  UNSPECIFIED: 'ALARM_ACKNOWLEDGEMENT_UNSPECIFIED'
};

// Different services use different values for severity.
// Standard alert policies have severities using STANDARD_ALERT values
// Alert manager resources, such as alarms, have severities using ALERT_MANAGER values. NMS alert policies also use alert manager severity values.
// Notify service resources, such as channel conjunctions, have severities using NOTIFY values
const SEVERITY_MAP = {
  MINOR: {
    ALERT_MANAGER: 'SEVERITY_MINOR',
    NOTIFY: 'minor',
    STANDARD_ALERT: 'minor'
  },
  WARNING: {
    ALERT_MANAGER: 'SEVERITY_WARNING',
    NOTIFY: 'warning',
    STANDARD_ALERT: 'minor2'
  },
  MAJOR: {
    ALERT_MANAGER: 'SEVERITY_MAJOR',
    NOTIFY: 'major',
    STANDARD_ALERT: 'major'
  },
  SEVERE: {
    ALERT_MANAGER: 'SEVERITY_SEVERE',
    NOTIFY: 'severe',
    STANDARD_ALERT: 'major2'
  },
  CRITICAL: {
    ALERT_MANAGER: 'SEVERITY_CRITICAL',
    NOTIFY: 'critical',
    STANDARD_ALERT: 'critical'
  },
  CLEAR: {
    ALERT_MANAGER: 'SEVERITY_CLEAR',
    NOTIFY: '',
    STANDARD_ALERT: 'clear'
  }
};

const ALERT_SEVERITY_TO_INTERNAL_SEVERITY_MAP = {
  [SEVERITY_MAP.CLEAR.ALERT_MANAGER]: 1,
  [SEVERITY_MAP.MINOR.ALERT_MANAGER]: 10,
  [SEVERITY_MAP.WARNING.ALERT_MANAGER]: 20,
  [SEVERITY_MAP.MAJOR.ALERT_MANAGER]: 40,
  [SEVERITY_MAP.SEVERE.ALERT_MANAGER]: 70,
  [SEVERITY_MAP.CRITICAL.ALERT_MANAGER]: 90
};

const STANDARD_ALERT_SEVERITY_TO_ALERT_MANAGER_SEVERITY = {
  [SEVERITY_MAP.MINOR.STANDARD_ALERT]: SEVERITY_MAP.MINOR.ALERT_MANAGER,
  [SEVERITY_MAP.WARNING.STANDARD_ALERT]: SEVERITY_MAP.WARNING.ALERT_MANAGER,
  [SEVERITY_MAP.MAJOR.STANDARD_ALERT]: SEVERITY_MAP.MAJOR.ALERT_MANAGER,
  [SEVERITY_MAP.SEVERE.STANDARD_ALERT]: SEVERITY_MAP.SEVERE.ALERT_MANAGER,
  [SEVERITY_MAP.CRITICAL.STANDARD_ALERT]: SEVERITY_MAP.CRITICAL.ALERT_MANAGER,
  [SEVERITY_MAP.CLEAR.STANDARD_ALERT]: SEVERITY_MAP.CLEAR.ALERT_MANAGER
};

const NOTIFY_SEVERITY_TO_STANDARD_ALERT_SEVERITY = {
  [SEVERITY_MAP.MINOR.NOTIFY]: SEVERITY_MAP.MINOR.STANDARD_ALERT,
  [SEVERITY_MAP.WARNING.NOTIFY]: SEVERITY_MAP.WARNING.STANDARD_ALERT,
  [SEVERITY_MAP.MAJOR.NOTIFY]: SEVERITY_MAP.MAJOR.STANDARD_ALERT,
  [SEVERITY_MAP.SEVERE.NOTIFY]: SEVERITY_MAP.SEVERE.STANDARD_ALERT,
  [SEVERITY_MAP.CRITICAL.NOTIFY]: SEVERITY_MAP.CRITICAL.STANDARD_ALERT
};

const NOTIFY_SEVERITIES = [
  SEVERITY_MAP.CRITICAL.NOTIFY,
  SEVERITY_MAP.SEVERE.NOTIFY,
  SEVERITY_MAP.MAJOR.NOTIFY,
  SEVERITY_MAP.WARNING.NOTIFY,
  SEVERITY_MAP.MINOR.NOTIFY
];

const ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY = {
  [SEVERITY_MAP.MINOR.ALERT_MANAGER]: SEVERITY_MAP.MINOR.STANDARD_ALERT,
  [SEVERITY_MAP.WARNING.ALERT_MANAGER]: SEVERITY_MAP.WARNING.STANDARD_ALERT,
  [SEVERITY_MAP.MAJOR.ALERT_MANAGER]: SEVERITY_MAP.MAJOR.STANDARD_ALERT,
  [SEVERITY_MAP.SEVERE.ALERT_MANAGER]: SEVERITY_MAP.SEVERE.STANDARD_ALERT,
  [SEVERITY_MAP.CRITICAL.ALERT_MANAGER]: SEVERITY_MAP.CRITICAL.STANDARD_ALERT,
  [SEVERITY_MAP.CLEAR.ALERT_MANAGER]: SEVERITY_MAP.CLEAR.STANDARD_ALERT
};

const ALERT_MANAGER_SEVERITY_TO_NOTIFY_SEVERITY = {
  [SEVERITY_MAP.MINOR.ALERT_MANAGER]: SEVERITY_MAP.MINOR.NOTIFY,
  [SEVERITY_MAP.WARNING.ALERT_MANAGER]: SEVERITY_MAP.WARNING.NOTIFY,
  [SEVERITY_MAP.MAJOR.ALERT_MANAGER]: SEVERITY_MAP.MAJOR.NOTIFY,
  [SEVERITY_MAP.SEVERE.ALERT_MANAGER]: SEVERITY_MAP.SEVERE.NOTIFY,
  [SEVERITY_MAP.CRITICAL.ALERT_MANAGER]: SEVERITY_MAP.CRITICAL.NOTIFY,
  [SEVERITY_MAP.CLEAR.ALERT_MANAGER]: SEVERITY_MAP.CLEAR.NOTIFY
};

const SEVERITY_KEY_OVERRIDES = {
  [SEVERITY_MAP.SEVERE.NOTIFY]: SEVERITY_MAP.SEVERE.STANDARD_ALERT,
  [SEVERITY_MAP.WARNING.NOTIFY]: SEVERITY_MAP.WARNING.STANDARD_ALERT
};

const ALERT_SEVERITIES = [
  SEVERITY_MAP.CRITICAL.STANDARD_ALERT,
  SEVERITY_MAP.SEVERE.STANDARD_ALERT,
  SEVERITY_MAP.MAJOR.STANDARD_ALERT,
  SEVERITY_MAP.WARNING.STANDARD_ALERT,
  SEVERITY_MAP.MINOR.STANDARD_ALERT
];

const ALERT_SEVERITY_COLORS = {
  [SEVERITY_MAP.CRITICAL.STANDARD_ALERT]: 'severity.critical',
  [SEVERITY_MAP.SEVERE.STANDARD_ALERT]: 'severity.major2',
  [SEVERITY_MAP.MAJOR.STANDARD_ALERT]: 'severity.major',
  [SEVERITY_MAP.WARNING.STANDARD_ALERT]: 'severity.minor2',
  [SEVERITY_MAP.MINOR.STANDARD_ALERT]: 'severity.minor',
  [SEVERITY_MAP.CLEAR.STANDARD_ALERT]: 'severity.baseline'
};

const ALERT_SEVERITY_LABELS = {
  // Standard Labels
  [SEVERITY_MAP.CRITICAL.STANDARD_ALERT]: 'Critical',
  [SEVERITY_MAP.SEVERE.STANDARD_ALERT]: 'Severe',
  [SEVERITY_MAP.MAJOR.STANDARD_ALERT]: 'Major',
  [SEVERITY_MAP.WARNING.STANDARD_ALERT]: 'Warning',
  [SEVERITY_MAP.MINOR.STANDARD_ALERT]: 'Minor',
  // Alert Manager Labels
  [SEVERITY_MAP.CRITICAL.ALERT_MANAGER]: 'Critical',
  [SEVERITY_MAP.SEVERE.ALERT_MANAGER]: 'Severe',
  [SEVERITY_MAP.MAJOR.ALERT_MANAGER]: 'Major',
  [SEVERITY_MAP.WARNING.ALERT_MANAGER]: 'Warning',
  [SEVERITY_MAP.MINOR.ALERT_MANAGER]: 'Minor',
  clear: 'Clear'
};

// Date range shortcuts for alert-related filter sidebars
const ALERT_LOOKBACK_SHORTCUTS = [
  { label: 'Last hour', lookbackSeconds: 3600, includeTime: true },
  { label: 'Last 8 hours', lookbackSeconds: 28800, includeTime: true },
  { label: 'Last 24 hours', lookbackSeconds: 86400, includeTime: true },
  { label: 'Last 7 days', lookbackSeconds: 604800, includeTime: true },
  { label: 'Last 14 days', lookbackSeconds: 1209600, includeTime: true },
  { label: 'Last 30 days', lookbackSeconds: 2592000, includeTime: true },
  { label: 'Last 90 days', lookbackSeconds: 7776000, includeTime: true }
];

const ALERT_SEVERITY_RANKS = {
  // Labels
  Critical: 5,
  Severe: 4,
  Major: 3,
  Warning: 2,
  Minor: 1,
  Clear: 0,
  // Standard
  [SEVERITY_MAP.CRITICAL.STANDARD_ALERT]: 5,
  [SEVERITY_MAP.SEVERE.STANDARD_ALERT]: 4,
  [SEVERITY_MAP.MAJOR.STANDARD_ALERT]: 3,
  [SEVERITY_MAP.WARNING.STANDARD_ALERT]: 2,
  [SEVERITY_MAP.MINOR.STANDARD_ALERT]: 1,
  // Alert Manager
  [SEVERITY_MAP.CRITICAL.ALERT_MANAGER]: 5,
  [SEVERITY_MAP.SEVERE.ALERT_MANAGER]: 4,
  [SEVERITY_MAP.MAJOR.ALERT_MANAGER]: 3,
  [SEVERITY_MAP.WARNING.ALERT_MANAGER]: 2,
  [SEVERITY_MAP.MINOR.ALERT_MANAGER]: 1
};

const SEVERITIES_TEMPLATE = {
  [SEVERITY_MAP.CRITICAL.STANDARD_ALERT]: 0,
  [SEVERITY_MAP.SEVERE.STANDARD_ALERT]: 0,
  [SEVERITY_MAP.MAJOR.STANDARD_ALERT]: 0,
  [SEVERITY_MAP.WARNING.STANDARD_ALERT]: 0,
  [SEVERITY_MAP.MINOR.STANDARD_ALERT]: 0
};

const SUPPORTED_AGGREGATION_FUNCTIONS = [{ value: 'avg', label: 'Average' }];

const PERCENTAGE_METRICS = [
  'flows_week_over_week',
  'bps_day_over_day',
  'bps_week_over_week',
  'hopcount_percent_change',
  'last_average_packet_loss_percent',
  'new_average_packet_loss_percent',
  'last_max_packet_loss_percent',
  'new_max_packet_loss_percent'
];

const LATENCY_METRICS = [
  'last_average_latency_without_zero',
  'new_average_latency_without_zero',
  'last_stddev_latency_without_zero',
  'new_stddev_latency_without_zero',
  'last_max_latency',
  'new_max_latency',
  'last_latency',
  'new_latency'
];

const SUPPRESSION_TYPES = {
  // An explicit "no alert type", and the default for new suppressions. Stored rather than inferred
  // from absence, so a legacy record with no metadata still reads as its real type.
  NONE: 'none',
  METRIC: 'conditionNms',
  NMS: 'conditionNmsNative',
  SYNTH: 'testv2',
  POLICY: 'policy',
  AGENT: 'agent',
  CONDITION: 'condition'
};

const SUPPRESSION_TIMESTAMP_SETTINGS = {
  UNSPECIFIED: 'TIMESTAMP_SETTING_UNSPECIFIED',
  KEEP_UNCHANGED: 'TIMESTAMP_SETTING_KEEP_UNCHANGED',
  AS_SOON_AS_POSSIBLE: 'TIMESTAMP_SETTING_AS_SOON_AS_POSSIBLE'
};

const SUPPRESSION_TYPE_LABELS = {
  [SUPPRESSION_TYPES.NONE]: 'None',
  [SUPPRESSION_TYPES.CONDITION]: 'Flow Conditions',
  [SUPPRESSION_TYPES.NMS]: 'NMS Conditions',
  [SUPPRESSION_TYPES.METRIC]: 'NMS Conditions (Classic Threshold)',
  [SUPPRESSION_TYPES.POLICY]: 'Alert Policy',
  [SUPPRESSION_TYPES.SYNTH]: 'Synthetics Test',
  [SUPPRESSION_TYPES.AGENT]: 'Synthetics Agent'
};

const MAPPED_METRICS = { bps: 'bytes', bytes: 'total_bytes', peak_bps: 'bytes' };

const METRIC_LABELS = {
  last_average_latency_without_zero: 'Last Week Avg Latency',
  new_average_latency_without_zero: 'New Avg Latency',
  last_stddev_latency_without_zero: 'Last Week Latency Std Dev',
  new_stddev_latency_without_zero: 'New Latency Std Dev',
  last_max_latency: 'Last Week Max Latency',
  new_max_latency: 'New Max Latency',
  last_latency: 'Latency',
  new_latency: 'New Latency',
  last_average_packet_loss_percent: 'Last Week Avg Packet Loss',
  new_average_packet_loss_percent: 'New Avg Packet Loss',
  last_max_packet_loss_percent: 'Last Week Max Packet Loss',
  new_max_packet_loss_percent: 'New Max Packet Loss',
  last_hop_count: 'Hop Count',
  new_hop_count: 'New Hop Count',
  ingress_flow_avg: 'Ingress Flow Average',
  egress_flow_avg: 'Egress Flow Average',
  ingress_snmp_avg: 'Ingress SNMP Average',
  egress_snmp_avg: 'Egress SNMP Average',
  last_agent_count: 'Agent Count',
  last_attempt_count: 'Test Attempts',
  last_error_count: 'Errors',
  last_timeout_count: 'Timeouts',
  last_success_count: 'Successful Tests',
  new_agent_count: 'New Agent Count',
  new_attempt_count: 'New Test Attempts',
  new_error_count: 'New Errors',
  new_timeout_count: 'New Timeouts',
  new_success_count: 'New Successful Tests',
  num_ifaces_in_plan: 'Number of interfaces in plan',
  iface_limit: 'Interfaces Limit'
};

const HOUR_IN_SECONDS = 3600;

const DAY_IN_SECONDS = HOUR_IN_SECONDS * 24;

const ALERT_LOOKBACK_OPTIONS = [
  {
    label: 'Alert +/- 1 hour',
    value: -HOUR_IN_SECONDS
  },
  {
    label: 'Alert +/- 24 hours',
    value: -DAY_IN_SECONDS
  },
  {
    label: 'Last hour',
    value: HOUR_IN_SECONDS
  },
  {
    label: 'Last day',
    value: DAY_IN_SECONDS
  },
  {
    label: 'Last 7 days',
    value: DAY_IN_SECONDS * 7
  },
  {
    label: 'Last 14 days',
    value: DAY_IN_SECONDS * 14
  },
  {
    label: 'Last 30 days',
    value: DAY_IN_SECONDS * 30
  }
];

// We use these as fallback max key values if the company doesn't have a keysPerPolicy/keysPerMetricPolicy permission defined
const MAX_KEYS_PER_POLICY = Object.freeze({
  // alerts.limits.keyPerMetricPolicy
  [POLICY_APPLICATIONS.METRIC]: 10000,
  // alerts.limits.keyPerPolicy
  default: 1000
});

const POLICY_DEFAULT_VALUES = Object.freeze({
  nTopKeysEvaluated: 25
});

const COMPARISON_DIRECTIONS = Object.freeze({
  TO_HISTORY: 'currentToHistory', // Default value
  TO_CURRENT: 'historyToCurrent'
});

const FLOW_CONDITION_TYPES = Object.freeze({
  STATIC: 'static',
  BASELINE: 'baseline',
  BASELINE_PERCENT: 'baselinePercent',
  CAPACITY: 'interfaceCapacity',
  CAPACITY_PERCENT: 'interfaceCapacityPercent',
  RATIO: 'ratio',
  TOP_KEYS: 'keyNotInTop',
  FORECAST: 'forecast'
});

const FLOW_THRESHOLD_STATUS = Object.freeze({
  UNSPECIFIED: 'THRESHOLD_STATUS_UNSPECIFIED',
  ACTIVE: 'THRESHOLD_STATUS_ACTIVE',
  DISABLED: 'THRESHOLD_STATUS_DISABLED'
});

const FLOW_CONDITION_TYPE_LABELS = Object.freeze({
  [FLOW_CONDITION_TYPES.STATIC]: 'Static',
  [FLOW_CONDITION_TYPES.BASELINE]: 'Baseline',
  [FLOW_CONDITION_TYPES.BASELINE_PERCENT]: 'Baseline',
  [FLOW_CONDITION_TYPES.CAPACITY]: 'Interface Capacity',
  [FLOW_CONDITION_TYPES.CAPACITY_PERCENT]: 'Interface Capacity',
  [FLOW_CONDITION_TYPES.RATIO]: 'Ratio-Based',
  [FLOW_CONDITION_TYPES.TOP_KEYS]: 'Top Keys',
  [FLOW_CONDITION_TYPES.FORECAST]: 'Forecast'
});

const RATIO_CONDITION_DIRECTIONS = Object.freeze({
  LEFT: 'DirectionLeft',
  RIGHT: 'DirectionRight',
  BOTH: 'DirectionBoth'
});

const OPERATOR_SHORT_LABELS = Object.freeze({
  equals: '=',
  notEquals: '≠',
  less: '<',
  greaterThan: '>',
  greaterThanOrEquals: '≥'
});

const SERIALIZE_FILTER_OPERATOR_MAP = Object.freeze({
  '=': 'equals',
  '<>': 'notEquals',
  '&': 'bitwiseAnd',
  '|': 'bitwiseOr',
  '<': 'lessThan',
  '>': 'greaterThan',
  '<=': 'lessThanOrEquals',
  '>=': 'greaterThanOrEquals',
  'NOT ILIKE': 'notContains',
  ILIKE: 'contains',
  '!~': 'notContainsRe',
  '~': 'containsRe',
  '!~*': 'notContainsReStar',
  '~*': 'containsReStar',
  BIT_ANY: 'bitwiseAny',
  BIT_NONE: 'bitwiseNone',
  BIT_ALL: 'bitwiseAll',
  BIT_NOT_ALL: 'bitwiseNotAll',
  EXISTS: 'exists',
  'NOT EXISTS': 'notExists'
});

const SERIALIZE_FILTER_CONNECTOR_MAP = { All: 'all', Any: 'any' };

const BUMP_EPOCH_KEYS = Object.freeze({
  DEVICE_UPDATE: 'deviceUpdate',
  INTERFACE_UPDATE: 'interfaceUpdate',
  POLICY_UPDATE: 'policyUpdate',
  PROCESSING_INTERRUPTED: 'processingInterrupted'
});

const APPLICATION_TO_BUMP_EPOCH_SETTINGS = Object.freeze({
  [POLICY_APPLICATIONS.METRIC]: {
    [BUMP_EPOCH_KEYS.DEVICE_UPDATE]: true,
    [BUMP_EPOCH_KEYS.PROCESSING_INTERRUPTED]: true
  },
  [POLICY_APPLICATIONS.DDOS]: {
    [BUMP_EPOCH_KEYS.POLICY_UPDATE]: true
  },
  [POLICY_APPLICATIONS.CORE]: {
    [BUMP_EPOCH_KEYS.POLICY_UPDATE]: true
  },
  // All set to false for these
  [POLICY_APPLICATIONS.KEVENT]: {},
  [POLICY_APPLICATIONS.SYNTHETICS]: {},
  [POLICY_APPLICATIONS.NMS]: {}
});

// Based on SERIALIZE_FILTER_OPERATOR_MAP, but with key & value swapped
const DESERIALIZE_FILTER_OPERATOR_MAP = Object.keys(SERIALIZE_FILTER_OPERATOR_MAP).reduce(
  (acc, key) => ({ ...acc, [SERIALIZE_FILTER_OPERATOR_MAP[key]]: key }),
  {}
);

const DESERIALIZE_FILTER_CONNECTOR_MAP = Object.keys(SERIALIZE_FILTER_CONNECTOR_MAP).reduce(
  (acc, key) => ({ ...acc, [SERIALIZE_FILTER_CONNECTOR_MAP[key]]: key }),
  {}
);

/**
 * Note: rule responses are snake_cased, whereas policy responses are camelCased
 *
 * @typedef {T[keyof T]} Enum<T>
 * @template T
 *
 * @typedef {{
 *   filterField: string;
 *   operator: import('../filters/constants.js').FilterOperator;
 *   filterValue: string;
 * }} ApiFilter
 * @typedef {{
 *   connector: Enum<FILTER_CONNECTOR>;
 *   not: boolean;
 *   filters: ApiFilter[];
 *   filterGroups: ApiFilterGroup[];
 *   savedFilters: { id: number, not: boolean }[];
 * }} ApiFilterGroup
 * @typedef {{
 *   connector: Enum<FILTER_CONNECTOR>;
 *   filterGroups: ApiFilterGroup[];
 * }} ApiFilterRoot
 */
/**
 * rolling_window and toggle should be one of two
 * @typedef {{
 *   severity: ApiAlertSeverity;
 *   ack_required: boolean;
 *   disabled: boolean;
 *   rolling_window?: { triggerCount: number, windowLength: string, gracePeriod: string };
 *   toggle?: { activationDelay: string, clearanceDelay: string };
 * }} ApiAlertManagerRuleLevel
 */
/**
 * @typedef {Enum<STANDARD_ALERT_SEVERITY_TO_ALERT_MANAGER_SEVERITY>} ApiAlertSeverity
 * @typedef {{
 *   enrich_event_view_model: boolean;
 *   silence_notifications: boolean;
 * }} ApiAlertManagerNotificationSettings
 * @typedef {{
 *   id?: string;
 *   revision?: number;
 *   created_at?: string;
 *   modified_at?: string;
 *   created_by?: string;
 *   modified_by?: string;
 *   alias?: string;
 *   application?: string;
 *   disabled?: boolean;
 *   expire_at?: string;
 *   silent_mode_expire_at?: string;
 *   levels?: ApiAlertManagerRuleLevel[];
 *   alarms_limit?: number;
 *   notification_settings?: ApiAlertManagerNotificationSettings;
 *   alarm_key_primary_dimension?: string;
 * }} ApiAlertManagerRule
 */

module.exports = {
  ALERT_LOOKBACK_SHORTCUTS,
  ALERT_MANAGER_ACTIVATION_MODES,
  ALARM_NULL_END_DATE,
  ALARM_SUPPRESSED_FILTER,
  ALERT_STATE_UI_TO_API,
  ALERT_STATE_API_TO_UI,
  ALERT_UI_STATES,
  ALERT_API_STATES,
  ALERT_ACKSTATE_LABELS,
  ALERT_ACKSTATES,
  ALERT_BASELINE_REASONS,
  ALERT_BASELINE_REASONS_MAP,
  BASELINE_FALLBACK_OPS,
  BASELINE_FALLBACK_OP_LABELS,
  BASELINE_FALLBACK_OPTIONS,
  ALERT_ID_SEARCH_REGEX,
  ALERT_SEVERITIES,
  ALERT_SEVERITY_COLORS,
  ALERT_SEVERITY_LABELS,
  ALERT_SEVERITY_RANKS,
  ALERT_STATE_ICONS,
  ALERT_STATE_LABELS,
  ALERT_SUMMARY_OPTIONS,
  ALERT_TABLE_COLUMNS,
  ALERT_CLEAR_REASONS,
  ALERT_CLEAR_REASONS_CODE_MAP,
  ALERT_CLEAR_REASONS_MAP,
  APPLICATION_TO_BUMP_EPOCH_SETTINGS,
  HOUR_IN_SECONDS,
  DAY_IN_SECONDS,
  DIMENSION_LOOKUP_PATHS,
  DIMENSION_TO_LABEL,
  EVALUATION_FREQUENCY_OPTIONS,
  INTERFACE_FILTER_MAP,
  LATENCY_METRICS,
  MAPPED_METRICS,
  MAX_WINDOW_LENGTH_MIN,
  METRIC_LABELS,
  PERCENTAGE_METRICS,
  POLICY_MAX_NAME_LENGTH,
  POLICY_AGG_FUNCTIONS,
  POLICY_AGG_FUNCTIONS_DESERIALIZE,
  POLICY_APPLICATION_LABELS,
  POLICY_BASELINE_PRESET_TYPES,
  POLICY_APPLICATIONS,
  FLOW_POLICY_APPLICATIONS,
  EVENT_POLICY_TYPES,
  EVENT_POLICY_METRICS,
  SEVERITIES_TEMPLATE,
  SEVERITY_KEY_OVERRIDES,
  ALERT_MANAGER_SEVERITY_TO_NOTIFY_SEVERITY,
  ALERT_SEVERITY_TO_INTERNAL_SEVERITY_MAP,
  STANDARD_ALERT_SEVERITY_TO_ALERT_MANAGER_SEVERITY,
  ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY,
  NOTIFY_SEVERITIES,
  NOTIFY_SEVERITY_TO_STANDARD_ALERT_SEVERITY,
  SEVERITY_MAP,
  SUPPRESSION_TYPES,
  SUPPRESSION_TYPE_LABELS,
  SUPPRESSION_TIMESTAMP_SETTINGS,
  SUPPORTED_AGGREGATION_FUNCTIONS,
  ALERT_LOOKBACK_OPTIONS,
  MAX_KEYS_PER_POLICY,
  POLICY_DEFAULT_VALUES,
  COMPARISON_DIRECTIONS,
  FLOW_THRESHOLD_STATUS,
  FLOW_CONDITION_TYPES,
  FLOW_CONDITION_TYPE_LABELS,
  RATIO_CONDITION_DIRECTIONS,
  OPERATOR_SHORT_LABELS,
  SERIALIZE_FILTER_OPERATOR_MAP,
  DESERIALIZE_FILTER_OPERATOR_MAP,
  SERIALIZE_FILTER_CONNECTOR_MAP,
  DESERIALIZE_FILTER_CONNECTOR_MAP,
  ALERTING_PER_PAGE_COUNT_DEFAULT,
  ALERTING_EXPORT_PER_PAGE_COUNT_DEFAULT,
  EXTERNAL_SYSTEMS
};
